// ── PLAIN WORDS — agency jargon to short labels a farmer reads ───────────────
//
// plainWords("P20A") -> "Claim payment". An unknown term comes back unchanged,
// never blank, so a missing entry shows the code rather than nothing.
// Codes are matched exactly (case-sensitive) so "BU" the unit never collides
// with "bu" the bushel; plain words ("liability") match in any case.
//
// Add a term with a label of a few words. No sentences here.

const CODES = {
  // M13 carrier file records
  M13: "Carrier file",
  P20: "Claim",
  P20A: "Claim payment",
  P20X: "Claim stage",
  // Coverage
  MPCI: "Crop insurance",
  APH: "Yield history",
  "T-yield": "County yield",
  PP: "Prevent plant",
  RP: "Revenue Protection",
  "RP-HPE": "Revenue Protection, no harvest price",
  HPE: "No harvest price",
  YP: "Yield Protection",
  SCO: "Supplemental coverage",
  ECO: "Enhanced coverage",
  OU: "Optional unit",
  EU: "Enterprise unit",
  QL: "Quality loss",
  NOL: "Notice of loss",
  AIP: "Insurance company",
  // Livestock and dairy
  LRP: "Livestock Risk Protection",
  DRP: "Dairy Revenue Protection",
  AEV: "Ending value",
  PRF: "Pasture insurance",
  // FSA
  FSN: "FSA farm number",
  AR: "Acreage report",
  "578": "FSA acreage report",
  "CCC-578": "FSA acreage report",
  "ARC-CO": "ARC county",
};

// Ordinary words, matched in any case.
const WORDS = {
  liability: "Coverage amount",
  indemnity: "Claim payment",
  "approved yield": "Yield history",
  "unit structure": "How fields are grouped",
};

// CY2026 / "CY 2026" -> "2026 crop"; PY2026 -> "2026 program year".
const YEAR_TAG = /^(CY|PY)\s?(\d{4})$/i;

export function plainWords(term) {
  if (term == null) return "";
  const raw = String(term).trim();
  if (!raw) return "";
  if (Object.prototype.hasOwnProperty.call(CODES, raw)) return CODES[raw];
  const y = raw.match(YEAR_TAG);
  if (y) return y[1].toUpperCase() === "CY" ? `${y[2]} crop` : `${y[2]} program year`;
  const w = WORDS[raw.toLowerCase()];
  return w || raw;
}

// The full list, for a "what this means" legend or a checker.
export const PLAIN_WORDS = Object.freeze({ ...CODES, ...WORDS });

// ── No emoji in tabs, headings, nav or empty states ─────────────────────────
// Pictographs, dingbats, flags, keycaps and the variation selector / joiner
// that glue them. Plain typographic marks (‹ › · — ×) are not emoji.
const EMOJI = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{1F1E6}-\u{1F1FF}\u{FE0F}\u{200D}\u{20E3}]/u;
const EMOJI_ALL = new RegExp(EMOJI.source, "gu");
export const hasEmoji = (s) => EMOJI.test(String(s == null ? "" : s));
// Removes emoji and the space they leave: "<emoji> Field Mapper" -> "Field Mapper".
export const noEmoji = (s) => String(s == null ? "" : s).replace(EMOJI_ALL, "").replace(/\s{2,}/g, " ").trim();

// ── Crop names in sentence case ─────────────────────────────────────────────
// Carrier and FSA files shout crop names ("DRY BEANS", "CORN SILAGE"); a page
// prints them the way a person writes them: "Dry beans", "Corn silage".
// Only the first letter is capitalised, so "Dry Beans" also reads "Dry beans".
export function sentenceCase(s) {
  const t = String(s == null ? "" : s).trim().replace(/_/g, " ").replace(/\s+/g, " ");
  if (!t) return "";
  const lower = t.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
// Program and plan names stay in capitals: "PRF", never "Prf".
const KEEP_CAPS = /\b(prf|drp|lrp|aph|sco|eco|arc|plc|fsa|rma)\b/gi;
export const cropWords = (crop) => sentenceCase(crop).replace(KEEP_CAPS, (w) => w.toUpperCase());
